import * as Yup from 'yup';
import { useModalV2 } from 'common/modal/hooks/useModalV2';
import { useUpdateSearchCandidateProfileQueryMutation } from 'generated-graphql/hooks/apollo-hooks';
import { useToast } from 'services/toast/hooks';
import { ToastType } from 'services/toast/types';
import { ActionOption } from 'components/ActionPopover/ActionPopover';
import { Fields } from 'common/form';

const renameSchema = Yup.object({
  displayTitle: Yup.string().trim().required('Name is required')
});

type RenameSchema = Yup.InferType<typeof renameSchema>;

const renameFields: Fields<RenameSchema> = {
  displayTitle: {
    type: 'text',
    autoFocus: true,
    label: 'Edit search title'
  }
};

export type MenuOptionProps = {
  searchCandidateProfileQueryId: string;
  displayTitle: string | null;
  isPinned: boolean;
};

type RenameProps = {
  searchCandidateProfileQueryId: string;
  displayTitle: string;
};

type PinProps = {
  searchCandidateProfileQueryId: string;
  isPinned: boolean;
};

type UseSearchCandidateProfileQueryConversationHistoryActionsProps = {
  updatePinStatus: (id: string, isPinned: boolean) => void;
  updateDisplayTitle: (id: string, displayTitle: string) => void;
  removeItem: (id: string) => void;
  onDelete: (id: string) => void;
};

export const useSearchCandidateProfileQueryConversationHistoryActions = ({
  updatePinStatus,
  updateDisplayTitle,
  removeItem,
  onDelete
}: UseSearchCandidateProfileQueryConversationHistoryActionsProps) => {
  const [updateSearchCandidateProfileQuery] =
    useUpdateSearchCandidateProfileQueryMutation();
  const { openModal } = useModalV2();
  const { showToast } = useToast();

  const handlePinAction = async ({
    searchCandidateProfileQueryId,
    isPinned
  }: PinProps) => {
    // Optimistically update the UI
    updatePinStatus(searchCandidateProfileQueryId, !isPinned);

    try {
      await updateSearchCandidateProfileQuery({
        variables: {
          input: {
            id: searchCandidateProfileQueryId,
            isPinned: !isPinned
          }
        }
      });
    } catch {
      // Revert optimistic update on error
      updatePinStatus(searchCandidateProfileQueryId, isPinned);
      showToast({
        type: ToastType.Error,
        title: `Failed to ${isPinned ? 'unpin' : 'pin'} search`,
        message: 'Please try again.'
      });
    }
  };

  const handleRenameAction = async ({
    searchCandidateProfileQueryId,
    displayTitle
  }: RenameProps) => {
    // Optimistically update the UI
    updateDisplayTitle(searchCandidateProfileQueryId, displayTitle);

    try {
      await updateSearchCandidateProfileQuery({
        variables: {
          input: {
            id: searchCandidateProfileQueryId,
            displayTitle
          }
        }
      });
    } catch {
      showToast({
        type: ToastType.Error,
        title: 'Failed to rename search',
        message: 'Please try again.'
      });
    }
  };

  const handleDeleteAction = async (searchCandidateProfileQueryId: string) => {
    // Optimistically update the UI
    removeItem(searchCandidateProfileQueryId);

    try {
      await updateSearchCandidateProfileQuery({
        variables: {
          input: {
            id: searchCandidateProfileQueryId,
            isArchived: true
          }
        }
      });
      onDelete(searchCandidateProfileQueryId);
      showToast({ type: ToastType.Success, title: 'Search deleted' });
    } catch {
      showToast({
        type: ToastType.Error,
        title: 'Failed to delete search',
        message: 'Please try again.'
      });
    }
  };

  const handlePinClick = ({
    searchCandidateProfileQueryId,
    isPinned
  }: PinProps) => {
    handlePinAction({
      searchCandidateProfileQueryId,
      isPinned
    });
  };

  const handleRenameClick = ({
    searchCandidateProfileQueryId,
    displayTitle
  }: RenameProps) => {
    openModal({
      title: 'Rename search',
      form: {
        schema: renameSchema,
        fields: renameFields,
        defaultValues: {
          displayTitle
        }
      },
      primaryButtonText: 'Save',
      secondaryButtonText: 'Cancel',
      onPrimaryAction: (data) =>
        handleRenameAction({
          searchCandidateProfileQueryId,
          displayTitle: data?.displayTitle
        })
    });
  };

  const handleDeleteClick = (searchCandidateProfileQueryId: string) => {
    openModal({
      title: 'Delete search',
      subtitle:
        'Are you sure you want to delete this search? This action cannot be undone.',
      primaryButtonText: 'Cancel',
      secondaryButtonText: 'Delete',
      primaryButtonProps: { variant: 'primary' },
      secondaryButtonProps: { variant: 'tertiary', state: 'destructive' },
      onSecondaryAction: () => handleDeleteAction(searchCandidateProfileQueryId)
    });
  };

  const getMenuOptions = ({
    searchCandidateProfileQueryId,
    displayTitle,
    isPinned
  }: MenuOptionProps): ActionOption[] => [
    {
      label: isPinned ? 'Unpin' : 'Pin',
      onClick: () => handlePinClick({ searchCandidateProfileQueryId, isPinned })
    },
    {
      label: 'Rename',
      onClick: () =>
        handleRenameClick({
          searchCandidateProfileQueryId,
          displayTitle: displayTitle || ''
        })
    },
    {
      label: 'Delete',
      onClick: () => handleDeleteClick(searchCandidateProfileQueryId),
      itemColor: 'error.main'
    }
  ];

  return {
    getMenuOptions
  };
};
